import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useIntl } from 'react-intl';
import {
  UploadOutlined,
  FileTextOutlined,
  DeleteOutlined,
  DownloadOutlined,
} from '@ant-design/icons';
import { Button, Upload, Steps } from 'antd';
import Papa from 'papaparse';
import { CSVLink } from 'react-csv';
import DrawerLayout from '../../shared/DrawerLayout';
import { getIsVisibleModal, closeModal } from '../../../redux/store/ui/common';
import { selectToken } from '../../../redux/store/userData/user/selectors';
import { selectAuth } from '../../../redux/store/userData/user';
import { fetchUsers } from '../../../redux/store/userData/users';
import { requestAPI } from '../../../utils/request';
import { drawerStyles } from '../../BulkActions/BulkActions.styles';
import { makeT } from '../../BulkActions/messages';
import {
  BULK_CREATE_DRAWER,
  BULK_CREATE_DRAWER_WIDTH,
} from '../../BulkActions/constants';
import CreateResultStep, { CreateResult } from './steps/CreateResultStep';
import CreatePreviewStep from './steps/CreatePreviewStep';

const { Dragger } = Upload;

// Bulk-create wizard. Rendered through the shared DrawerLayout with the same
// `.ba-*` chrome as BulkArchiveDrawer so both bulk drawers look identical.
const BulkCreateUsersDrawer: React.FC = () => {
  const intl = useIntl();
  const t = makeT(intl);
  const dispatch = useDispatch();
  const token = useSelector(selectToken);
  const visible = useSelector(getIsVisibleModal(BULK_CREATE_DRAWER));
  const auth = useSelector(selectAuth);
  const adminBrandCode = auth?.brands?.[0]?.code;

  const [step, setStep] = useState(0);
  const [file, setFile] = useState<File | null>(null);
  const [parsedCsvRows, setParsedCsvRows] = useState<any>(null);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<CreateResult | null>(null);
  // The just-created users, fetched back from the list API for the preview.
  const [createdUsers, setCreatedUsers] = useState<any[]>([]);
  const [loadingCreated, setLoadingCreated] = useState(false);

  const TEMPLATE_DATA = [
    [
      'brand',
      'userType',
      'email',
      'password',
      'firstName',
      'lastName',
      'birthPlace',
      'merchantReference',
      'birthDate',
      'signupDate',
      'companyName',
      'legalType',
      'registerNr',
      'registerLocation',
      'phoneNumber',
      'address.country',
      'address.city',
      'address.zipCode',
      'address.streetNumber',
      'address.streetName',
      'DL.number',
      'DL.issuePlace',
      'DL.issueDate',
      'DL.expiryDate',
      'ID.number',
      'ID.issueDate',
      'ID.issuePlace',
      'ID.birthPlace',
      'SEPA.IBAN',
      'SEPA.BIC',
      'signupPlatform',
      'marketingConsentGiven',
      'locale',
      'attributionFleetId',
      'tariffId',
      'email.verified',
      'ID.verified',
      'DL.verified',
      'address.verified',
      'blocked',
      'blockReason',
      'blockReasonDetails',
      'note',
      'notifications.transactionalMessages',
      'eBUS.Id',
      'eBUS.customerNumber',
    ],
    [
      adminBrandCode,
      'Individual',
      'user8@getaway.pro',
      'Abcd1234!',
      'Erika',
      'Musterwoman',
      'Berlin',
      '106',
      '01.01.1990',
      '05.05.2024',
      '',
      '',
      '',
      '',
      '+491729844501',
      'DE',
      'Berlin',
      '10011',
      'Mainstr.',
      '102',
      '1117777444',
      'Berlin',
      '01.01.2000',
      '01.01.2030',
      'FA1111003',
      '03.01.2000',
      'Laufen',
      'Anger',
      'DE89370400440532013000',
      '',
      'Android',
      'FALSE',
      'de',
      '',
      '',
      'FALSE',
      'FALSE',
      'FALSE',
      'FALSE',
      'TRUE',
      'fraud',
      'fraudIdentity',
      'stolen passport caseNr. 14444',
      'FALSE',
      '',
      '106',
    ],
    [
      adminBrandCode,
      'Solopreneur',
      'user9@getaway.pro',
      'Abcd1234!',
      'Erika',
      'Helgret',
      'Berlin',
      '107',
      '01.01.1990',
      '05.05.2024',
      'Example Company',
      '',
      '',
      '',
      '+491729844501',
      'DE',
      'Berlin',
      '10011',
      'Mainstr.',
      '102',
      '1117777444',
      'Berlin',
      '01.01.2000',
      '01.01.2030',
      'FA1111003',
      '03.01.2000',
      'Laufen',
      'Anger',
      'DE89370400440532013000',
      '',
      'Android',
      'FALSE',
      'de',
      '',
      '',
      'FALSE',
      'FALSE',
      'FALSE',
      'FALSE',
      'TRUE',
      'fraud',
      'fraudIdentity',
      'stolen passport caseNr. 14444',
      'FALSE',
      '',
      '106',
    ],
    [
      adminBrandCode,
      'Organization',
      'user10@getaway.pro',
      'Abcd1234!',
      'Erika',
      'Musterwoman',
      'Berlin',
      '108',
      '01.01.1990',
      '05.05.2024',
      'GETAWAY',
      'GmbH',
      '169886B',
      'Berlin-Charlottenburg',
      '+491729844501',
      'DE',
      'Berlin',
      '10011',
      'Mainstr.',
      '102',
      '1117777444',
      'Berlin',
      '01.01.2000',
      '01.01.2030',
      'FA1111003',
      '03.01.2000',
      'Laufen',
      'Anger',
      'DE89370400440532013000',
      '',
      'Android',
      'FALSE',
      'de',
      '',
      '',
      'FALSE',
      'FALSE',
      'FALSE',
      'FALSE',
      'TRUE',
      'fraud',
      'fraudIdentity',
      'stolen passport caseNr. 14444',
      'FALSE',
      '',
      '106',
    ],
  ];

  // Non-empty CSV data rows (excluding the header).
  const dataRowCount: number = parsedCsvRows?.data
    ? parsedCsvRows.data
        .slice(1)
        .filter(
          (r: any[]) =>
            Array.isArray(r) && r.some((c) => String(c ?? '').trim() !== '')
        ).length
    : 0;
  const fileSizeKB = file ? Math.round((file.size / 1024) * 10) / 10 : 0;

  const reset = () => {
    setStep(0);
    setFile(null);
    setParsedCsvRows(null);
    setSubmitting(false);
    setResult(null);
    setCreatedUsers([]);
    setLoadingCreated(false);
  };

  // Keep the Intercom launcher behind the drawer while it's open — same body
  // class the archive drawer uses (handled by drawerStyles).
  useEffect(() => {
    document.body.classList.toggle('ba-drawer-open', visible);
    return () => document.body.classList.remove('ba-drawer-open');
  }, [visible]);

  const handleClose = () => {
    dispatch(closeModal(BULK_CREATE_DRAWER));
    reset();
  };

  // The CSV endpoint returns only a count, so we fetch the freshly-created
  // users back from the list (newest first): offset 0, limit = number created.
  const loadCreatedUsers = async (count: number) => {
    if (count <= 0) {
      setCreatedUsers([]);
      return;
    }
    setLoadingCreated(true);
    try {
      const data = await requestAPI('/fleets/users', {
        offset: '0',
        limit: String(count),
      });
      setCreatedUsers(Array.isArray(data?.users) ? data.users : []);
    } catch (e) {
      setCreatedUsers([]);
    } finally {
      setLoadingCreated(false);
    }
  };

  const handleFile = (f: File) => {
    // No skipEmptyLines: keep row indices aligned with the server's per-row
    // error keys ("<rowIndex>:<field>") so failed rows map back correctly.
    Papa.parse(f as any, {
      complete: (results: any) => setParsedCsvRows(results),
    });
  };

  const uploadProps = {
    name: 'file',
    accept: '.csv',
    multiple: false,
    showUploadList: false,
    customRequest: (options: any) => options.onSuccess(),
    beforeUpload: (f: File) => {
      setFile(f);
      handleFile(f);
      // We POST the file manually on submit, so prevent antd's auto-upload.
      return false;
    },
  };

  const removeFile = () => {
    setFile(null);
    setParsedCsvRows(null);
  };

  // Server parses the whole CSV in one POST and returns
  // { createdUsers, errors: { entry: [{ key: "<rowIndex>:<field>", value }] } }.
  const handleOnSubmit = async () => {
    if (!file) return;
    setStep(1);
    setSubmitting(true);
    setResult(null);

    const totalRows = dataRowCount;
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await fetch(
        `${process.env.VITE_API_URL}/fleets/users/csv`,
        {
          method: 'POST',
          headers: { Authorization: token },
          body: formData,
        }
      );
      const data = await response.json();

      if (!response.ok) {
        setResult({
          total: totalRows,
          created: 0,
          failed: totalRows,
          erroredRows: null,
        });
        return;
      }

      const created = data.createdUsers ?? 0;

      // Map the server's per-row errors back onto the original CSV rows so the
      // admin can download the failed rows, fix them, and re-upload.
      let erroredRows: string[][] | null = null;
      if (data?.errors?.entry?.length > 0 && parsedCsvRows?.data?.length) {
        erroredRows = [parsedCsvRows.data[0].concat(['error'])];
        data.errors.entry.forEach((errorEntry: any) => {
          let errorIndex = parseInt(errorEntry.key.split(':')[0], 10);
          if (errorIndex < 0) errorIndex = 0;
          // +1 to skip the header row at index 0
          const errorRow = parsedCsvRows.data[errorIndex + 1];
          if (errorRow) {
            erroredRows!.push(errorRow.concat([errorEntry.value]));
          }
        });
      }

      const failed = erroredRows
        ? erroredRows.length - 1
        : Math.max(totalRows - created, 0);

      dispatch(fetchUsers(0, 50, () => {}));
      setResult({ total: totalRows, created, failed, erroredRows });
      // Pre-fetch the created users so the Preview step is ready when opened.
      loadCreatedUsers(created);
    } catch (e) {
      setResult({
        total: totalRows,
        created: 0,
        failed: totalRows,
        erroredRows: null,
      });
    } finally {
      setSubmitting(false);
    }
  };

  const stepItems = [
    { title: t('fleetadmin.bulk.step.upload') },
    { title: t('fleetadmin.bulk.create.step.run') },
    { title: t('fleetadmin.bulk.step.preview') },
  ];

  const renderUpload = () => (
    <div>
      {!file ? (
        <>
          <Dragger {...uploadProps} className="ba-dropzone-wrap">
            <div className="ba-dropzone">
              <UploadOutlined />
              <div className="ba-dropzone-title">
                {t('fleetadmin.bulk.upload.dropzone.title')}
              </div>
              <div className="ba-dropzone-hint">
                {t('fleetadmin.bulk.create.dropzone.hint')}
              </div>
            </div>
          </Dragger>
          <div className="ba-template-row">
            <CSVLink
              data={TEMPLATE_DATA}
              filename="bulk-create-users-template.csv"
              className="ba-csv-link"
              data-testid="BulkCreateUsers-downloadTemplate"
            >
              <DownloadOutlined />{' '}
              {t('fleetadmin.bulk.upload.downloadTemplate')}
            </CSVLink>
          </div>
        </>
      ) : (
        <div className="ba-file-chip">
          <FileTextOutlined style={{ fontSize: 20, color: '#0AB735' }} />
          <div>
            <div className="ba-file-name">{file.name} ✓</div>
            <div className="ba-file-meta">
              {t('fleetadmin.bulk.upload.fileMeta', {
                count: dataRowCount,
                size: fileSizeKB,
              })}
            </div>
          </div>
          <DeleteOutlined
            data-testid="BulkCreateUsers-removeFile"
            className="ba-file-remove"
            onClick={removeFile}
          />
        </div>
      )}
    </div>
  );

  const renderFooter = () => {
    if (step === 0) {
      return (
        <>
          <span className="ba-footer-info">
            {file
              ? t('fleetadmin.bulk.footer.rows', { count: dataRowCount })
              : ''}
          </span>
          <div className="ba-footer-actions">
            <Button onClick={handleClose}>
              {t('fleetadmin.bulk.footer.cancel')}
            </Button>
            <Button
              type="primary"
              data-testid="BulkCreateUsers-submitButton"
              disabled={!file}
              onClick={handleOnSubmit}
            >
              {t('fleetadmin.bulk.create.button')}
            </Button>
          </div>
        </>
      );
    }
    if (step === 1) {
      const canPreview = !!result && result.created > 0;
      return (
        <>
          <span className="ba-footer-info" />
          <div className="ba-footer-actions">
            <Button disabled={submitting} onClick={reset}>
              {t('fleetadmin.bulk.footer.runAnother')}
            </Button>
            {canPreview ? (
              <Button
                type="primary"
                data-testid="BulkCreateUsers-preview-next"
                disabled={submitting}
                onClick={() => setStep(2)}
              >
                {t('fleetadmin.bulk.create.footer.preview')}
              </Button>
            ) : (
              <Button
                type="primary"
                data-testid="BulkCreateUsers-done"
                disabled={submitting}
                onClick={handleClose}
              >
                {t('fleetadmin.bulk.footer.done')}
              </Button>
            )}
          </div>
        </>
      );
    }
    return (
      <>
        <span className="ba-footer-info" />
        <div className="ba-footer-actions">
          <Button onClick={() => setStep(1)}>
            {t('fleetadmin.bulk.footer.back')}
          </Button>
          <Button
            type="primary"
            data-testid="BulkCreateUsers-done"
            onClick={handleClose}
          >
            {t('fleetadmin.bulk.footer.done')}
          </Button>
        </div>
      </>
    );
  };

  const body = (
    <div className="ba-drawer-body" data-testid="BulkCreateUsers-uploadWrapper">
      <div className="ba-steps">
        <Steps current={step} items={stepItems} />
      </div>

      <div className="ba-step">
        {step === 0 && renderUpload()}
        {step === 1 && (
          <CreateResultStep
            submitting={submitting}
            dataRowCount={dataRowCount}
            result={result}
          />
        )}
        {step === 2 && (
          <CreatePreviewStep
            loadingCreated={loadingCreated}
            createdUsers={createdUsers}
            createdCount={result?.created}
          />
        )}
      </div>

      <style jsx global>
        {drawerStyles}
      </style>
    </div>
  );

  return (
    <DrawerLayout
      visible={visible}
      width={BULK_CREATE_DRAWER_WIDTH}
      title={t('fleetadmin.bulk.create.title')}
      body={body}
      footer={<div className="ba-footer">{renderFooter()}</div>}
      onReload={undefined}
      onClose={handleClose}
    />
  );
};

export default BulkCreateUsersDrawer;
